import { modalStore } from "@/lib/stores/modals";
import { formatTimestampAuto } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useStore } from "@tanstack/react-store";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { AnnouncementResponse, getAnnouncement, readAnnouncement } from "./api";

// 公告列表项骨架屏
function NoticeItemSkeleton() {
	return (
		<div className="relative min-w-0 md:w-full w-[145px] flex-shrink-0 text-left mb-[5px] p-3 text-t1 rounded-[10px] bg-bg2">
			<div className="flex items-center justify-between min-w-0">
				<Skeleton className="w-20 h-4 rounded" />
			</div>
			<div className="mt-2">
				<Skeleton className="w-16 h-3 rounded" />
			</div>
		</div>
	);
}

// 公告内容骨架屏
function NoticeContentSkeleton() {
	return (
		<div className="flex flex-col justify-between h-full pb-[54px] pr-4">
			<div>
				<Skeleton className="w-3/4 h-6 rounded mb-4 mx-auto" />
				<div className="space-y-3">
					<Skeleton className="w-full h-4 rounded" />
					<Skeleton className="w-5/6 h-4 rounded" />
					<Skeleton className="w-4/5 h-4 rounded" />
					<Skeleton className="w-full h-4 rounded" />
					<Skeleton className="w-3/4 h-4 rounded" />
					<Skeleton className="w-5/6 h-4 rounded" />
					<Skeleton className="w-4/5 h-4 rounded" />
					<Skeleton className="w-full h-4 rounded" />
				</div>
			</div>
			<div className="mt-8 text-right">
				<Skeleton className="w-20 h-4 rounded ml-auto mb-2" />
				<Skeleton className="w-32 h-3 rounded ml-auto" />
			</div>
		</div>
	);
}

interface NoticeContentProps {
	selectedNotice: AnnouncementResponse;
}

function NoticeContent({ selectedNotice }: NoticeContentProps) {
	const { content, created_time, title } = selectedNotice;

	// 处理 content 中的 a 标签，添加 target="_blank" 和 rel="noopener noreferrer"
	const processedContent = content.replace(
		/<a\s+href=/gi,
		'<a target="_blank" rel="noopener noreferrer" href=',
	);

	return (
		<div className="flex flex-col justify-between h-full pb-[54px] pr-4 md:pr-6">
			<div>
				{/* <h2 className="text-xl mb-4 text-t1 text-center">{title}</h2> */}
				<div
					className="text-t2 whitespace-pre-line [&>*:not(a)]:!text-t2 [&_*:not(a)]:!text-t2 [&_a]:!text-blue-500 [&_a]:hover:!text-blue-600 [&_a_*]:!text-blue-500 [&_a_*]:hover:!text-blue-600"
					dangerouslySetInnerHTML={{ __html: processedContent }}
				/>
			</div>
			<div className="mt-8 text-left text-t2">
				<p>PeachLust {m.common_team()}</p>
				<p className="mt-2">{formatTimestampAuto(created_time)}</p>
			</div>
		</div>
	);
}

const PAGE_SIZE = 20;

export default function Announcement() {
	const [selected, setSelected] = useState<string | null>(null);
	const [readMap, setReadMap] = useState<Record<string, boolean>>({});
	const { announcementModalOpen } = useStore(modalStore);
	const queryClient = useQueryClient();

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		error,
	} = useInfiniteQuery<any, Error>({
		queryKey: ["announcements"],
		queryFn: ({ pageParam = 1 }) =>
			getAnnouncement({ page_num: pageParam as number, page_size: PAGE_SIZE }),
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage?.data?.list?.length < PAGE_SIZE) return undefined;
			return allPages.length + 1;
		},
		initialPageParam: 1,
		enabled: announcementModalOpen,
	});

	const records = data?.pages.flatMap((page) => page?.data?.list ?? []) ?? [];

	useEffect(() => {
		if (records.length && !selected) {
			const firstAnnouncement = records[0];
			setSelected(firstAnnouncement.announcement_id);

			// 如果第一条公告未读，自动标记为已读
			if (
				!firstAnnouncement.has_read &&
				!readMap[firstAnnouncement.announcement_id]
			) {
				readAnnouncement(firstAnnouncement.announcement_id);
				setReadMap((prev) => ({
					...prev,
					[firstAnnouncement.announcement_id]: true,
				}));

				// 标记已读后，使 header 中的公告查询失效并重新获取
				queryClient.invalidateQueries({ queryKey: ["announcement"] });
			}
		}
	}, [records, selected, readMap]);

	// 使用 useRef 存储防抖函数
	const debouncedFetchRef = useRef(
		debounce(() => {
			console.log("fetching next page");
			fetchNextPage();
		}, 200),
	);

	const handleScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			console.log("scroll triggered", e.currentTarget.scrollTop);
			const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
			if (
				scrollTop + clientHeight >= scrollHeight - 10 &&
				hasNextPage &&
				!isFetchingNextPage
			) {
				debouncedFetchRef.current();
			}
		},
		[hasNextPage, isFetchingNextPage],
	);

	// 组件卸载时清理防抖函数
	useEffect(() => {
		const debouncedFetch = debouncedFetchRef.current;
		return () => {
			debouncedFetch.cancel();
		};
	}, []);

	const handleItemClick = async (announcementId: string) => {
		setSelected(announcementId);
		if (!readMap[announcementId]) {
			await readAnnouncement(announcementId);
			setReadMap((prev) => ({ ...prev, [announcementId]: true }));

			// 标记已读后，使 header 中的公告查询失效并重新获取
			queryClient.invalidateQueries({ queryKey: ["announcement"] });
		}
	};

	const selectedNotice = records.find((n) => n.announcement_id === selected);

	return (
		<div>
			<Dialog
				open={announcementModalOpen}
				onOpenChange={(open) => modalStore.setAnnouncementModalOpen(open)}
			>
				<DialogContent className="md:!w-[850px] md:!max-w-[850px] md:max-h-[658px]">
					<DialogHeader>
						<DialogTitle>{m.announcement()}</DialogTitle>
					</DialogHeader>

					<div className="flex w-[calc(100vw-32px)] md:w-full h-[500px] flex-col md:flex-row">
						{/* 左侧公告列表 */}
						<div
							className="overflow-x-auto min-w-0 md:w-[237px] gap-2 md:gap-0 md:h-[500px] flex md:flex-col md:border-r md:border-line1 md:pr-[10px] md:overflow-y-auto md:py-[22px]"
							onScroll={handleScroll}
						>
							{isLoading
								? // 显示骨架屏
									Array.from({ length: 8 }).map((_, index) => (
										<NoticeItemSkeleton key={`skeleton-${index}`} />
									))
								: // 显示实际内容
									records.map((notice) => (
										<button
											type="button"
											key={notice.announcement_id}
											className={`relative min-w-0 md:w-full w-[145px] flex-shrink-0 text-left mb-[5px] p-3 text-t1 rounded-[10px] cursor-pointer transition-all ${
												selected === notice.announcement_id
													? "bg-c1 "
													: "bg-bg2"
											} relative`}
											onClick={() => handleItemClick(notice.announcement_id)}
										>
											<div className="flex items-center justify-between min-w-0">
												<span className="truncate text-sm text-t1">
													{notice.title}
												</span>
												{!(
													notice.has_read || readMap[notice.announcement_id]
												) && (
													<span className="absolute right-2 top-[5px] ml-2 w-[6px] h-[6px] bg-main1 rounded-full inline-block flex-shrink-0" />
												)}
											</div>
											<div className="text-xs text-t2 mt-2">
												{formatTimestampAuto(notice.created_time)}
											</div>
										</button>
									))}
							{isFetchingNextPage && (
								<div className="text-center py-2">{m.common_loading()}</div>
							)}
						</div>
						<div className="flex-1 pl-8 overflow-y-auto py-[22px]">
							{isLoading ? (
								<NoticeContentSkeleton />
							) : selectedNotice ? (
								<NoticeContent selectedNotice={selectedNotice} />
							) : (
								<div className="text-t3 text-center mt-20">
									{m.announcement_select()}
								</div>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
